import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { MessageCircle, Plus, Send, Sparkles, User, Bot, Loader2, FileText, Trash2, Clipboard, CheckCircle2, AlertCircle, ArrowLeft, ChevronRight, Bell, Handshake, XCircle, RefreshCw } from 'lucide-react';

const STATE_LABEL = {
    collecting: { color: 'bg-[hsl(45_100%_88%)] text-[hsl(35_60%_25%)]', icon: MessageCircle },
    ready_to_quote: { color: 'bg-[hsl(145_45%_88%)] text-[hsl(145_55%_22%)]', icon: CheckCircle2 },
    awaiting_approval: { color: 'bg-[hsl(35_100%_88%)] text-[hsl(35_90%_25%)]', icon: AlertCircle },
    quoted: { color: 'bg-[hsl(210_60%_90%)] text-[hsl(210_60%_25%)]', icon: FileText },
    followup_started: { color: 'bg-[hsl(280_50%_92%)] text-[hsl(280_50%_30%)]', icon: Bell },
    awaiting_reply: { color: 'bg-[hsl(45_100%_88%)] text-[hsl(35_60%_25%)]', icon: Clipboard },
    negotiating: { color: 'bg-[hsl(25_90%_90%)] text-[hsl(25_90%_30%)]', icon: RefreshCw },
    confirmed: { color: 'bg-[hsl(145_55%_84%)] text-[hsl(145_55%_18%)]', icon: Handshake },
    rejected: { color: 'bg-[hsl(0_55%_92%)] text-[hsl(0_55%_30%)]', icon: XCircle },
};

export default function LeadConversations() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromAgenti = searchParams.get('from') === 'agenti';
    const [items, setItems] = useState(null);
    const [openNew, setOpenNew] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/agenti/auto-quote/list');
            setItems(res.data);
        } catch (e) {
            toast.error(t('common.error_load') || 'Errore caricamento');
            setItems([]);
        }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const handleCloseDetail = useCallback(() => {
        setSelected(null);
        load();
    }, [load]);

    return (
        <div data-testid="lead-conversations-page" className="pb-20 md:pb-0">
            {fromAgenti && (
                <Button
                    onClick={() => navigate('/app/agenti')}
                    variant="ghost"
                    className="gap-2 -ml-2 mb-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    data-testid="lc-back-to-agents"
                >
                    <ArrowLeft className="w-4 h-4" /> {t('pages.ag_crm_back_agents') || 'Torna agli Agenti'}
                </Button>
            )}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="font-display text-4xl md:text-5xl flex items-center gap-2">
                        <Bot className="w-9 h-9 text-[hsl(var(--accent))]" /> {t('pages.aq_title')}
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('pages.aq_subtitle')}</p>
                </div>
                <Button onClick={() => setOpenNew(true)} className="hidden sm:flex bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold gap-2 h-11" data-testid="aq-new-button">
                    <Plus className="w-4 h-4" /> {t('pages.aq_new')}
                </Button>
            </div>

            <Card className="overflow-hidden">
                {items === null ? (
                    <div className="p-6 space-y-3">
                        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-center text-[hsl(var(--muted-foreground))]">
                        <Bot className="w-12 h-12 mx-auto mb-3" />
                        <p className="font-semibold">{t('pages.aq_empty_title')}</p>
                        <p className="text-sm mt-1 max-w-md mx-auto">{t('pages.aq_empty_help')}</p>
                        <Button onClick={() => setOpenNew(true)} className="mt-4 bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]"><Plus className="w-4 h-4 mr-1" /> {t('pages.aq_new')}</Button>
                    </div>
                ) : (
                    <ul className="divide-y divide-[hsl(var(--border))]">
                        {items.map((c) => {
                            const meta = STATE_LABEL[c.state] || STATE_LABEL.collecting;
                            const IconC = meta.icon;
                            return (
                                <li key={c.id}>
                                    <button onClick={() => setSelected(c.id)} className="w-full text-left p-4 hover:bg-[hsl(var(--muted))]/40 flex items-center gap-3" data-testid={`aq-row-${c.id}`}>
                                        <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                                            <User className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold truncate">{c.customer_name || c.customer_email}</div>
                                            <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.customer_email} · {c.n_messages} {t('pages.aq_messages')}</div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Badge className={meta.color + ' gap-1 text-xs'}>
                                                <IconC className="w-3 h-3" /> {t('pages.aq_state_' + c.state)}
                                            </Badge>
                                            <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            {/* Mobile FAB */}
            <Button onClick={() => setOpenNew(true)} className="sm:hidden fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] p-0" aria-label={t('pages.aq_new')} data-testid="aq-fab">
                <Plus className="w-6 h-6" />
            </Button>

            <NewConversationDialog open={openNew} onOpenChange={setOpenNew} onCreated={(c) => { load(); setSelected(c.id); }} />
            <ConversationDetail
                convId={selected}
                onClose={handleCloseDetail}
                onQuoted={(quoteId) => { toast.success(t('pages.aq_quote_created')); load(); }}
            />
        </div>
    );
}

function NewConversationDialog({ open, onOpenChange, onCreated }) {
    const { t } = useTranslation();
    const [customerEmail, setCustomerEmail] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [initialMsg, setInitialMsg] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) { setCustomerEmail(''); setCustomerName(''); setInitialMsg(''); }
    }, [open]);

    const create = async () => {
        if (!customerEmail.trim() || !initialMsg.trim()) {
            toast.error(t('pages.aq_no_input'));
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/agenti/auto-quote/standalone', {
                customer_email: customerEmail.trim(),
                customer_name: customerName.trim(),
                initial_customer_message: initialMsg.trim(),
            });
            onOpenChange(false);
            onCreated?.(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error') || 'Errore');
        } finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl sm:max-h-[90vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-4 sm:p-6 rounded-none sm:rounded-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <Bot className="w-6 h-6 text-[hsl(var(--accent))]" /> {t('pages.aq_new_title')}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('pages.aq_new_help')}</p>
                <div className="space-y-3 mt-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t('pages.aq_customer_email')} *</Label>
                            <Input type="email" inputMode="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="cliente@example.com" className="mt-1 h-11" data-testid="aq-customer-email" />
                        </div>
                        <div>
                            <Label>{t('pages.aq_customer_name')}</Label>
                            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Mario Rossi" className="mt-1 h-11" data-testid="aq-customer-name" />
                        </div>
                    </div>
                    <div>
                        <Label className="flex items-center gap-1">
                            <Clipboard className="w-3 h-3" /> {t('pages.aq_paste_reply')} *
                        </Label>
                        <Textarea rows={6} value={initialMsg} onChange={(e) => setInitialMsg(e.target.value)} className="mt-1" placeholder={t('pages.aq_paste_reply_ph')} data-testid="aq-initial-msg" />
                    </div>
                    <Button onClick={create} disabled={loading || !customerEmail.trim() || !initialMsg.trim()} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] h-12 font-bold gap-2" data-testid="aq-create-button">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_analyzing')}</> : <><Sparkles className="w-4 h-4" /> {t('pages.aq_start')}</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function ConversationDetail({ convId, onClose, onQuoted }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [conv, setConv] = useState(null);
    const [customerInput, setCustomerInput] = useState('');
    const [editedSubject, setEditedSubject] = useState('');
    const [editedBody, setEditedBody] = useState('');
    const [busy, setBusy] = useState(null); // 'parse' | 'send' | 'quote' | 'delete'

    useEffect(() => {
        if (!convId) { setConv(null); return; }
        (async () => {
            try {
                const res = await api.get(`/agenti/auto-quote/${convId}`);
                setConv(res.data);
                setEditedSubject(res.data.draft_subject || '');
                setEditedBody(res.data.draft_body_text || '');
                setCustomerInput('');
            } catch (e) {
                toast.error(t('common.error_load') || 'Errore');
                onClose?.();
            }
        })();
    }, [convId, t, onClose]);

    const handleCustomerMsg = async () => {
        if (!customerInput.trim()) return;
        setBusy('parse');
        try {
            // Choose endpoint based on current state
            const followupStates = ['followup_started', 'awaiting_reply', 'negotiating'];
            const endpoint = followupStates.includes(conv.state)
                ? `/agenti/auto-quote/${convId}/followup-customer-msg`
                : `/agenti/auto-quote/${convId}/customer-msg`;
            const res = await api.post(endpoint, { text: customerInput.trim() });
            setConv(res.data);
            setEditedSubject(res.data.draft_subject || '');
            setEditedBody(res.data.draft_body_text || '');
            setCustomerInput('');
            toast.success(t('pages.aq_ai_processed'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleApproveAndSend = async () => {
        if (!window.confirm(t('pages.aq_approve_confirm'))) return;
        setBusy('approve');
        try {
            const res = await api.post(`/agenti/auto-quote/${convId}/approve-and-send`);
            setConv(res.data);
            toast.success(t('pages.aq_approved_sent'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleStartFollowup = async () => {
        setBusy('followup');
        try {
            const res = await api.post(`/agenti/auto-quote/${convId}/start-followup`);
            setConv(res.data);
            setEditedSubject(res.data.draft_subject || '');
            setEditedBody(res.data.draft_body_text || '');
            toast.success(t('pages.aq_followup_started'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleMarkOutcome = async (outcome) => {
        const labels = { confirmed: t('pages.aq_state_confirmed'), rejected: t('pages.aq_state_rejected') };
        if (!window.confirm(t('pages.aq_mark_confirm', { state: labels[outcome] || outcome }))) return;
        setBusy('outcome');
        try {
            const res = await api.post(`/agenti/auto-quote/${convId}/mark-outcome`, { outcome, note: '' });
            setConv(res.data);
            toast.success(t('pages.aq_state_updated'));
        } catch (e) {
            toast.error(t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleSend = async () => {
        if (!editedBody.trim()) return;
        setBusy('send');
        try {
            const res = await api.post(`/agenti/auto-quote/${convId}/send-agent`, {
                text: editedBody.trim(),
                subject: editedSubject.trim() || null,
            });
            setConv(res.data);
            setEditedSubject('');
            setEditedBody('');
            toast.success(t('pages.aq_sent'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleGenerateQuote = async () => {
        setBusy('quote');
        try {
            const res = await api.post(`/agenti/auto-quote/${convId}/generate-quote`);
            onQuoted?.(res.data.quote_id);
            // Refresh conversation in place so the owner can review + approve
            const refreshed = await api.get(`/agenti/auto-quote/${convId}`);
            setConv(refreshed.data);
            toast.success(t('pages.aq_quote_created'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.generic_error'));
        } finally { setBusy(null); }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('common.confirm_delete') || 'Sicuro?')) return;
        setBusy('delete');
        try {
            await api.delete(`/agenti/auto-quote/${convId}`);
            toast.success(t('common.deleted') || 'Eliminato');
            onClose?.();
        } catch (e) {
            toast.error(t('common.generic_error'));
        } finally { setBusy(null); }
    };

    if (!convId) return null;

    return (
        <Dialog open={!!convId} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl sm:max-h-[92vh] max-h-screen h-screen sm:h-auto overflow-y-auto p-0 sm:p-0 rounded-none sm:rounded-lg" data-testid="aq-detail">
                {!conv ? (
                    <div className="p-6 space-y-3"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-32 w-full" /><Skeleton className="h-24 w-full" /></div>
                ) : (
                    <>
                        {/* Sticky header */}
                        <div className="sticky top-0 z-10 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))] px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                                <Button size="sm" variant="ghost" onClick={onClose} className="h-9 sm:hidden"><ArrowLeft className="w-4 h-4" /></Button>
                                <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                                    <User className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold truncate">{conv.customer_name || conv.customer_email}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{conv.customer_email}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                {(() => {
                                    const meta = STATE_LABEL[conv.state] || STATE_LABEL.collecting;
                                    const IconC = meta.icon;
                                    return <Badge className={meta.color + ' gap-1 text-xs'}><IconC className="w-3 h-3" /> {t('pages.aq_state_' + conv.state)}</Badge>;
                                })()}
                                <Button size="sm" variant="ghost" onClick={handleDelete} className="h-9 text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                        </div>

                        <div className="p-4 sm:p-6 space-y-4">
                            {/* Extracted info box */}
                            {Object.keys(conv.extracted_info || {}).length > 0 && (
                                <Card className="bg-[hsl(var(--muted))]/40 border-[hsl(var(--accent))]/40 p-3 sm:p-4" data-testid="aq-extracted-info">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
                                        <span className="font-semibold text-sm">{t('pages.aq_extracted')}</span>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        {Object.entries(conv.extracted_info).map(([k, v]) => v ? (
                                            <div key={k} className="flex gap-2">
                                                <span className="text-[hsl(var(--muted-foreground))] capitalize">{k.replace(/_/g, ' ')}:</span>
                                                <span className="font-medium truncate">{String(v)}</span>
                                            </div>
                                        ) : null)}
                                    </div>
                                </Card>
                            )}

                            {/* Generate Quote CTA when ready */}
                            {conv.state === 'ready_to_quote' && (
                                <Card className="p-4 border-2 border-[hsl(145_55%_34%)] bg-[hsl(145_45%_94%)]" data-testid="aq-ready-card">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-[hsl(145_55%_22%)] shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-semibold text-[hsl(145_55%_18%)]">{t('pages.aq_ready_title')}</div>
                                            <p className="text-sm text-[hsl(145_55%_28%)] mt-1">{t('pages.aq_ready_help')}</p>
                                            <Button onClick={handleGenerateQuote} disabled={busy === 'quote'} className="mt-3 bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white gap-2 h-11 font-bold" data-testid="aq-generate-quote">
                                                {busy === 'quote' ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_generating')}</> : <><FileText className="w-4 h-4" /> {t('pages.aq_generate_quote')}</>}
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            )}
                            {conv.state === 'awaiting_approval' && (
                                <Card className="p-4 border-2 border-[hsl(35_100%_50%)] bg-[hsl(45_100%_96%)]" data-testid="aq-approval-card">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-6 h-6 text-[hsl(35_90%_30%)] shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-display text-lg text-[hsl(35_90%_22%)]">{t('pages.aq_approval_title')}</div>
                                            <p className="text-sm text-[hsl(35_90%_30%)] mt-1">{t('pages.aq_approval_help')}</p>
                                            <div className="mt-3 rounded-lg bg-white border border-[hsl(35_60%_85%)] p-3 grid sm:grid-cols-3 gap-3 text-sm">
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.prev_col_numero')}</div>
                                                    <div className="font-mono font-semibold">{conv.quote_numero || '—'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.prev_detail_total')}</div>
                                                    <div className="font-display text-xl tabular-nums">€{Number(conv.quote_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('pages.aq_share_link')}</div>
                                                    {conv.quote_share_token ? (
                                                        <a href={`/p/${conv.quote_share_token}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(var(--accent))] hover:underline truncate inline-block max-w-full">
                                                            /p/{conv.quote_share_token.slice(0, 8)}…
                                                        </a>
                                                    ) : <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>}
                                                </div>
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                <Button onClick={() => navigate('/app/preventivi')} variant="outline" size="sm" className="h-10 gap-1" data-testid="aq-edit-quote">
                                                    <FileText className="w-4 h-4" /> {t('pages.aq_edit_quote')}
                                                </Button>
                                                <Button onClick={handleApproveAndSend} disabled={busy === 'approve'} className="bg-[hsl(145_55%_34%)] hover:bg-[hsl(145_55%_28%)] text-white gap-2 h-11 font-bold" data-testid="aq-approve-send">
                                                    {busy === 'approve' ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_approving')}</> : <><Send className="w-4 h-4" /> {t('pages.aq_approve_send')}</>}
                                                </Button>
                                            </div>
                                            <p className="text-[11px] text-[hsl(35_60%_35%)] mt-2 italic">{t('pages.aq_approval_what_happens')}</p>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {conv.state === 'quoted' && (
                                <Card className="p-4 border-2 border-[hsl(210_60%_60%)] bg-[hsl(210_70%_96%)]" data-testid="aq-quoted-card">
                                    <div className="flex items-start gap-3">
                                        <FileText className="w-6 h-6 text-[hsl(210_60%_30%)] shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-semibold text-[hsl(210_60%_25%)]">{t('pages.aq_quoted_title')}</div>
                                            <p className="text-sm text-[hsl(210_60%_30%)] mt-1">{t('pages.aq_quoted_help')}</p>
                                            <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                <Button onClick={handleStartFollowup} disabled={busy === 'followup'} className="bg-[hsl(280_50%_45%)] hover:bg-[hsl(280_50%_38%)] text-white gap-2 h-11 font-bold" data-testid="aq-start-followup">
                                                    {busy === 'followup' ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_starting')}</> : <><Bell className="w-4 h-4" /> {t('pages.aq_start_followup')}</>}
                                                </Button>
                                                {conv.quote_total && <span className="text-sm font-mono">€{Number(conv.quote_total).toLocaleString('it-IT')}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {(conv.state === 'followup_started' || conv.state === 'awaiting_reply' || conv.state === 'negotiating') && Object.keys(conv.followup_info || {}).length > 0 && (
                                <Card className="bg-[hsl(280_50%_96%)] border-[hsl(280_50%_70%)] p-3 sm:p-4" data-testid="aq-followup-info">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Bell className="w-4 h-4 text-[hsl(280_50%_40%)]" />
                                        <span className="font-semibold text-sm">{t('pages.aq_followup_status')}</span>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        {Object.entries(conv.followup_info).map(([k, v]) => v ? (
                                            <div key={k} className="flex gap-2">
                                                <span className="text-[hsl(var(--muted-foreground))] capitalize">{k.replace(/_/g, ' ')}:</span>
                                                <span className="font-medium truncate">{String(v)}</span>
                                            </div>
                                        ) : null)}
                                    </div>
                                    <div className="mt-3 flex gap-2 flex-wrap">
                                        <Button onClick={() => handleMarkOutcome('confirmed')} disabled={busy === 'outcome'} size="sm" variant="outline" className="h-9 gap-1 border-[hsl(145_55%_34%)] text-[hsl(145_55%_22%)]" data-testid="aq-mark-confirmed">
                                            <Handshake className="w-3.5 h-3.5" /> {t('pages.aq_mark_confirmed')}
                                        </Button>
                                        <Button onClick={() => handleMarkOutcome('rejected')} disabled={busy === 'outcome'} size="sm" variant="outline" className="h-9 gap-1 border-[hsl(0_55%_45%)] text-[hsl(0_55%_35%)]" data-testid="aq-mark-rejected">
                                            <XCircle className="w-3.5 h-3.5" /> {t('pages.aq_mark_rejected')}
                                        </Button>
                                    </div>
                                </Card>
                            )}

                            {conv.state === 'confirmed' && (
                                <Card className="p-4 border-2 border-[hsl(145_55%_34%)] bg-[hsl(145_55%_92%)]" data-testid="aq-confirmed-card">
                                    <div className="flex items-start gap-3">
                                        <Handshake className="w-6 h-6 text-[hsl(145_55%_22%)] shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-semibold text-[hsl(145_55%_18%)]">{t('pages.aq_confirmed_title')}</div>
                                            <p className="text-sm text-[hsl(145_55%_25%)] mt-1">{t('pages.aq_confirmed_help')}</p>
                                            {conv.followup_info?.metodo_pagamento_anticipo && (
                                                <div className="text-xs mt-2 text-[hsl(145_55%_22%)]">
                                                    💰 {t('pages.aq_payment_method')}: <strong>{conv.followup_info.metodo_pagamento_anticipo}</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            )}

                            {conv.state === 'rejected' && (
                                <Card className="p-3 bg-[hsl(0_55%_96%)] border-[hsl(0_55%_60%)]/40 flex items-center gap-2" data-testid="aq-rejected-card">
                                    <XCircle className="w-4 h-4 text-[hsl(0_55%_35%)]" />
                                    <span className="text-sm font-medium">{t('pages.aq_rejected_state')}</span>
                                </Card>
                            )}

                            {/* Timeline */}
                            <div className="space-y-3">
                                {(conv.messages || []).map((m, i) => (
                                    <div key={i} className={`flex gap-2 ${m.role === 'agent' ? 'flex-row-reverse' : ''}`}>
                                        <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.role === 'agent' ? 'bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)]' : 'bg-[hsl(var(--muted))]'}`}>
                                            {m.role === 'agent' ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                        </div>
                                        <div className={`flex-1 min-w-0 ${m.role === 'agent' ? 'text-right' : ''}`}>
                                            <div className={`inline-block max-w-[88%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === 'agent' ? 'bg-[hsl(var(--accent))]/15 text-left' : 'bg-[hsl(var(--muted))] text-left'}`}>
                                                {m.subject && <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-1">{m.subject}</div>}
                                                {m.text}
                                            </div>
                                            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{m.sent_at && new Date(m.sent_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Customer reply paste box */}
                            <Card className="p-3 sm:p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Clipboard className="w-4 h-4 text-[hsl(var(--accent))]" />
                                    <span className="font-semibold text-sm">{t('pages.aq_paste_new_reply')}</span>
                                </div>
                                <Textarea
                                    rows={3}
                                    value={customerInput}
                                    onChange={(e) => setCustomerInput(e.target.value)}
                                    placeholder={t('pages.aq_paste_new_reply_ph')}
                                    className="text-sm"
                                    data-testid="aq-customer-input"
                                />
                                <div className="flex justify-end mt-2">
                                    <Button onClick={handleCustomerMsg} disabled={busy === 'parse' || !customerInput.trim()} size="sm" variant="outline" className="gap-1 h-9" data-testid="aq-parse-button">
                                        {busy === 'parse' ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_analyzing')}</> : <><Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" /> {t('pages.aq_ai_analyze')}</>}
                                    </Button>
                                </div>
                            </Card>

                            {/* Draft response */}
                            {(editedBody || conv.draft_body_text) && (
                                <Card className="p-3 sm:p-4 border-[hsl(var(--accent))]/40 bg-[hsl(45_100%_98%)]" data-testid="aq-draft-card">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                            <Bot className="w-4 h-4 text-[hsl(var(--accent))]" />
                                            <span className="font-semibold text-sm">{t('pages.aq_draft_response')}</span>
                                        </div>
                                        {conv.last_ai_reasoning && (
                                            <div className="text-[10px] text-[hsl(var(--muted-foreground))] italic max-w-[60%] text-right truncate" title={conv.last_ai_reasoning}>💡 {conv.last_ai_reasoning}</div>
                                        )}
                                    </div>
                                    <Input
                                        value={editedSubject}
                                        onChange={(e) => setEditedSubject(e.target.value)}
                                        placeholder={t('pages.aq_subject_ph')}
                                        className="mb-2 h-10 font-medium"
                                        data-testid="aq-draft-subject"
                                    />
                                    <Textarea
                                        rows={6}
                                        value={editedBody}
                                        onChange={(e) => setEditedBody(e.target.value)}
                                        className="text-sm"
                                        data-testid="aq-draft-body"
                                    />
                                    <div className="flex justify-end gap-2 mt-3">
                                        <Button onClick={handleSend} disabled={busy === 'send' || !editedBody.trim()} className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-10" data-testid="aq-send-button">
                                            {busy === 'send' ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('pages.aq_sending')}</> : <><Send className="w-4 h-4" /> {t('pages.aq_send_reply')}</>}
                                        </Button>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
